// src/lib/taxonomy/technology-hierarchy.ts
// Technology hierarchy for capability inference
// Focus: ~50 technologies common in federal contracting

import { TechnologyNode } from './types';

export const TECHNOLOGY_HIERARCHY: TechnologyNode[] = [
  // ============================================================
  // FRONTEND TECHNOLOGIES
  // ============================================================
  {
    id: 'react',
    name: 'React',
    aliases: ['ReactJS', 'React.js', 'React JS'],
    category: 'frontend',
    proves: [
      { capability: 'frontend_development', confidence: 0.95 },
      { capability: 'javascript_frameworks', confidence: 0.95 },
      { capability: 'component_based_architecture', confidence: 0.90 },
      { capability: 'single_page_applications', confidence: 0.85 },
      { capability: 'web_development', confidence: 0.85 },
      { capability: 'modern_ui_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['angular', 'vue', 'javascript', 'typescript']
  },
  {
    id: 'angular',
    name: 'Angular',
    aliases: ['AngularJS', 'Angular.js', 'Angular 2+', 'Angular2'],
    category: 'frontend',
    proves: [
      { capability: 'frontend_development', confidence: 0.95 },
      { capability: 'javascript_frameworks', confidence: 0.95 },
      { capability: 'typescript_development', confidence: 0.85 },
      { capability: 'enterprise_web_applications', confidence: 0.85 },
      { capability: 'single_page_applications', confidence: 0.85 },
      { capability: 'web_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['react', 'vue', 'typescript']
  },
  {
    id: 'vue',
    name: 'Vue.js',
    aliases: ['Vue', 'VueJS', 'Vue 3'],
    category: 'frontend',
    proves: [
      { capability: 'frontend_development', confidence: 0.95 },
      { capability: 'javascript_frameworks', confidence: 0.95 },
      { capability: 'progressive_web_apps', confidence: 0.80 },
      { capability: 'single_page_applications', confidence: 0.85 },
      { capability: 'web_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['react', 'angular', 'javascript']
  },
  {
    id: 'javascript',
    name: 'JavaScript',
    aliases: ['JS', 'ECMAScript', 'ES6', 'ES2015+'],
    category: 'frontend',
    proves: [
      { capability: 'web_development', confidence: 0.95 },
      { capability: 'frontend_development', confidence: 0.90 },
      { capability: 'scripting', confidence: 0.85 },
      { capability: 'cross_platform_development', confidence: 0.75 }
    ],
    relatedTechnologies: ['typescript', 'nodejs', 'react', 'angular']
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    aliases: ['TS'],
    category: 'frontend',
    proves: [
      { capability: 'typescript_development', confidence: 0.95 },
      { capability: 'strongly_typed_development', confidence: 0.90 },
      { capability: 'enterprise_javascript', confidence: 0.85 },
      { capability: 'web_development', confidence: 0.85 },
      { capability: 'code_quality_practices', confidence: 0.80 }
    ],
    relatedTechnologies: ['javascript', 'angular', 'react']
  },

  // ============================================================
  // BACKEND TECHNOLOGIES
  // ============================================================
  {
    id: 'nodejs',
    name: 'Node.js',
    aliases: ['Node', 'NodeJS', 'Node JS'],
    category: 'backend',
    proves: [
      { capability: 'backend_development', confidence: 0.95 },
      { capability: 'server_side_javascript', confidence: 0.95 },
      { capability: 'api_development', confidence: 0.90 },
      { capability: 'microservices', confidence: 0.80 },
      { capability: 'real_time_applications', confidence: 0.80 }
    ],
    relatedTechnologies: ['javascript', 'express', 'typescript']
  },
  {
    id: 'python',
    name: 'Python',
    aliases: ['Python 3', 'Python3', 'Py'],
    category: 'backend',
    proves: [
      { capability: 'backend_development', confidence: 0.90 },
      { capability: 'scripting', confidence: 0.95 },
      { capability: 'data_processing', confidence: 0.85 },
      { capability: 'automation', confidence: 0.90 },
      { capability: 'api_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['django', 'flask', 'fastapi']
  },
  {
    id: 'java',
    name: 'Java',
    aliases: ['Java SE', 'Java EE', 'J2EE', 'Jakarta EE'],
    category: 'backend',
    proves: [
      { capability: 'enterprise_development', confidence: 0.95 },
      { capability: 'backend_development', confidence: 0.95 },
      { capability: 'object_oriented_programming', confidence: 0.95 },
      { capability: 'cross_platform_development', confidence: 0.85 },
      { capability: 'large_scale_systems', confidence: 0.85 }
    ],
    relatedTechnologies: ['spring', 'spring_boot', 'maven']
  },
  {
    id: 'spring',
    name: 'Spring Framework',
    aliases: ['Spring', 'Spring Boot', 'SpringBoot', 'Spring MVC'],
    category: 'backend',
    proves: [
      { capability: 'enterprise_java', confidence: 0.95 },
      { capability: 'backend_development', confidence: 0.95 },
      { capability: 'microservices', confidence: 0.90 },
      { capability: 'api_development', confidence: 0.90 },
      { capability: 'dependency_injection', confidence: 0.90 }
    ],
    relatedTechnologies: ['java', 'maven', 'gradle']
  },
  {
    id: 'dotnet',
    name: '.NET',
    aliases: ['.NET Core', '.NET Framework', 'dotnet', 'ASP.NET', 'C#', 'CSharp'],
    category: 'backend',
    proves: [
      { capability: 'enterprise_development', confidence: 0.95 },
      { capability: 'backend_development', confidence: 0.95 },
      { capability: 'microsoft_technologies', confidence: 0.95 },
      { capability: 'api_development', confidence: 0.90 },
      { capability: 'windows_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['azure', 'sql_server']
  },
  {
    id: 'golang',
    name: 'Go',
    aliases: ['Golang', 'Go Language'],
    category: 'backend',
    proves: [
      { capability: 'backend_development', confidence: 0.95 },
      { capability: 'systems_programming', confidence: 0.90 },
      { capability: 'microservices', confidence: 0.90 },
      { capability: 'high_performance_computing', confidence: 0.85 },
      { capability: 'cloud_native_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['docker', 'kubernetes']
  },

  // ============================================================
  // DATABASE TECHNOLOGIES
  // ============================================================
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    aliases: ['Postgres', 'PG', 'PGSQL'],
    category: 'database',
    proves: [
      { capability: 'relational_databases', confidence: 0.95 },
      { capability: 'database_administration', confidence: 0.90 },
      { capability: 'sql', confidence: 0.95 },
      { capability: 'data_management', confidence: 0.90 },
      { capability: 'open_source_databases', confidence: 0.90 }
    ],
    relatedTechnologies: ['sql_server', 'mysql', 'oracle']
  },
  {
    id: 'sql_server',
    name: 'Microsoft SQL Server',
    aliases: ['SQL Server', 'MSSQL', 'MS SQL', 'T-SQL'],
    category: 'database',
    proves: [
      { capability: 'relational_databases', confidence: 0.95 },
      { capability: 'microsoft_technologies', confidence: 0.90 },
      { capability: 'enterprise_databases', confidence: 0.90 },
      { capability: 'sql', confidence: 0.95 },
      { capability: 'database_administration', confidence: 0.90 }
    ],
    relatedTechnologies: ['dotnet', 'azure', 'postgresql']
  },
  {
    id: 'oracle',
    name: 'Oracle Database',
    aliases: ['Oracle DB', 'Oracle', 'OracleDB', 'PL/SQL'],
    category: 'database',
    proves: [
      { capability: 'relational_databases', confidence: 0.95 },
      { capability: 'enterprise_databases', confidence: 0.95 },
      { capability: 'large_scale_data_management', confidence: 0.90 },
      { capability: 'sql', confidence: 0.95 },
      { capability: 'database_administration', confidence: 0.90 }
    ],
    relatedTechnologies: ['sql_server', 'postgresql']
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    aliases: ['Mongo', 'MongoDB Atlas'],
    category: 'database',
    proves: [
      { capability: 'nosql_databases', confidence: 0.95 },
      { capability: 'document_databases', confidence: 0.95 },
      { capability: 'scalable_data_storage', confidence: 0.85 },
      { capability: 'data_management', confidence: 0.85 },
      { capability: 'flexible_schema_design', confidence: 0.90 }
    ],
    relatedTechnologies: ['nodejs', 'elasticsearch']
  },
  {
    id: 'elasticsearch',
    name: 'Elasticsearch',
    aliases: ['Elastic', 'ELK Stack', 'Elastic Stack'],
    category: 'database',
    proves: [
      { capability: 'search_technologies', confidence: 0.95 },
      { capability: 'log_analytics', confidence: 0.90 },
      { capability: 'big_data', confidence: 0.80 },
      { capability: 'real_time_analytics', confidence: 0.85 },
      { capability: 'full_text_search', confidence: 0.95 }
    ],
    relatedTechnologies: ['kibana', 'logstash', 'mongodb']
  },

  // ============================================================
  // CLOUD TECHNOLOGIES
  // ============================================================
  {
    id: 'aws',
    name: 'Amazon Web Services',
    aliases: ['AWS', 'Amazon AWS', 'AWS Cloud'],
    category: 'cloud',
    proves: [
      { capability: 'cloud_computing', confidence: 0.95 },
      { capability: 'cloud_architecture', confidence: 0.90 },
      { capability: 'infrastructure_as_code', confidence: 0.80 },
      { capability: 'scalable_infrastructure', confidence: 0.90 },
      { capability: 'managed_services', confidence: 0.85 }
    ],
    relatedTechnologies: ['aws_govcloud', 'terraform', 'docker']
  },
  {
    id: 'aws_govcloud',
    name: 'AWS GovCloud',
    aliases: ['GovCloud', 'AWS GovCloud (US)', 'Amazon GovCloud'],
    category: 'cloud',
    proves: [
      { capability: 'cloud_computing', confidence: 0.95 },
      { capability: 'fedramp_compliant_infrastructure', confidence: 0.95 },
      { capability: 'government_cloud', confidence: 0.95 },
      { capability: 'secure_cloud_operations', confidence: 0.90 },
      { capability: 'compliance_focused_infrastructure', confidence: 0.90 }
    ],
    relatedTechnologies: ['aws', 'azure_government']
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    aliases: ['Azure', 'MS Azure', 'Azure Cloud'],
    category: 'cloud',
    proves: [
      { capability: 'cloud_computing', confidence: 0.95 },
      { capability: 'microsoft_technologies', confidence: 0.90 },
      { capability: 'cloud_architecture', confidence: 0.90 },
      { capability: 'hybrid_cloud', confidence: 0.85 },
      { capability: 'enterprise_cloud', confidence: 0.90 }
    ],
    relatedTechnologies: ['azure_government', 'dotnet', 'sql_server']
  },
  {
    id: 'azure_government',
    name: 'Azure Government',
    aliases: ['Azure Gov', 'Microsoft Azure Government', 'MAG'],
    category: 'cloud',
    proves: [
      { capability: 'cloud_computing', confidence: 0.95 },
      { capability: 'fedramp_compliant_infrastructure', confidence: 0.95 },
      { capability: 'government_cloud', confidence: 0.95 },
      { capability: 'microsoft_technologies', confidence: 0.90 },
      { capability: 'compliance_focused_infrastructure', confidence: 0.90 }
    ],
    relatedTechnologies: ['azure', 'aws_govcloud']
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    aliases: ['GCP', 'Google Cloud', 'Google Cloud Services'],
    category: 'cloud',
    proves: [
      { capability: 'cloud_computing', confidence: 0.95 },
      { capability: 'cloud_architecture', confidence: 0.90 },
      { capability: 'big_data', confidence: 0.85 },
      { capability: 'machine_learning_infrastructure', confidence: 0.85 },
      { capability: 'scalable_infrastructure', confidence: 0.90 }
    ],
    relatedTechnologies: ['aws', 'azure', 'kubernetes']
  },

  // ============================================================
  // DEVOPS & INFRASTRUCTURE
  // ============================================================
  {
    id: 'docker',
    name: 'Docker',
    aliases: ['Docker Containers', 'Containerization'],
    category: 'devops',
    proves: [
      { capability: 'containerization', confidence: 0.95 },
      { capability: 'devops', confidence: 0.90 },
      { capability: 'microservices', confidence: 0.85 },
      { capability: 'infrastructure_automation', confidence: 0.85 },
      { capability: 'deployment_automation', confidence: 0.85 }
    ],
    relatedTechnologies: ['kubernetes', 'docker_compose', 'podman']
  },
  {
    id: 'kubernetes',
    name: 'Kubernetes',
    aliases: ['K8s', 'K8', 'Kube', 'Container Orchestration'],
    category: 'devops',
    proves: [
      { capability: 'container_orchestration', confidence: 0.95 },
      { capability: 'devops', confidence: 0.90 },
      { capability: 'microservices', confidence: 0.90 },
      { capability: 'scalable_infrastructure', confidence: 0.90 },
      { capability: 'cloud_native_development', confidence: 0.90 },
      { capability: 'high_availability_systems', confidence: 0.85 }
    ],
    relatedTechnologies: ['docker', 'helm', 'istio']
  },
  {
    id: 'terraform',
    name: 'Terraform',
    aliases: ['HashiCorp Terraform', 'TF', 'HCL'],
    category: 'devops',
    proves: [
      { capability: 'infrastructure_as_code', confidence: 0.95 },
      { capability: 'devops', confidence: 0.90 },
      { capability: 'cloud_automation', confidence: 0.90 },
      { capability: 'multi_cloud_management', confidence: 0.85 },
      { capability: 'configuration_management', confidence: 0.85 }
    ],
    relatedTechnologies: ['aws', 'azure', 'ansible']
  },
  {
    id: 'ansible',
    name: 'Ansible',
    aliases: ['Red Hat Ansible', 'Ansible Automation'],
    category: 'devops',
    proves: [
      { capability: 'configuration_management', confidence: 0.95 },
      { capability: 'infrastructure_automation', confidence: 0.95 },
      { capability: 'devops', confidence: 0.90 },
      { capability: 'deployment_automation', confidence: 0.90 },
      { capability: 'it_automation', confidence: 0.90 }
    ],
    relatedTechnologies: ['terraform', 'puppet', 'chef']
  },
  {
    id: 'jenkins',
    name: 'Jenkins',
    aliases: ['Jenkins CI', 'Jenkins CD', 'Jenkins Pipeline'],
    category: 'devops',
    proves: [
      { capability: 'continuous_integration', confidence: 0.95 },
      { capability: 'continuous_deployment', confidence: 0.90 },
      { capability: 'devops', confidence: 0.90 },
      { capability: 'build_automation', confidence: 0.95 },
      { capability: 'pipeline_development', confidence: 0.90 }
    ],
    relatedTechnologies: ['gitlab_ci', 'github_actions', 'azure_devops']
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    aliases: ['GitLab CI', 'GitLab CI/CD', 'GitLab DevOps'],
    category: 'devops',
    proves: [
      { capability: 'version_control', confidence: 0.95 },
      { capability: 'continuous_integration', confidence: 0.90 },
      { capability: 'devops', confidence: 0.85 },
      { capability: 'source_code_management', confidence: 0.95 },
      { capability: 'collaborative_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['git', 'github', 'jenkins']
  },
  {
    id: 'git',
    name: 'Git',
    aliases: ['Git Version Control', 'GitHub', 'Bitbucket'],
    category: 'devops',
    proves: [
      { capability: 'version_control', confidence: 0.95 },
      { capability: 'source_code_management', confidence: 0.95 },
      { capability: 'collaborative_development', confidence: 0.85 },
      { capability: 'code_review_practices', confidence: 0.80 }
    ],
    relatedTechnologies: ['gitlab', 'github_actions']
  },

  // ============================================================
  // SECURITY TECHNOLOGIES
  // ============================================================
  {
    id: 'splunk',
    name: 'Splunk',
    aliases: ['Splunk Enterprise', 'Splunk SIEM', 'Splunk Security'],
    category: 'security',
    proves: [
      { capability: 'security_monitoring', confidence: 0.95 },
      { capability: 'log_management', confidence: 0.95 },
      { capability: 'siem', confidence: 0.90 },
      { capability: 'security_analytics', confidence: 0.90 },
      { capability: 'incident_response', confidence: 0.80 }
    ],
    relatedTechnologies: ['elasticsearch', 'qradar']
  },
  {
    id: 'nessus',
    name: 'Nessus',
    aliases: ['Tenable Nessus', 'Nessus Scanner', 'Tenable.io'],
    category: 'security',
    proves: [
      { capability: 'vulnerability_assessment', confidence: 0.95 },
      { capability: 'security_scanning', confidence: 0.95 },
      { capability: 'compliance_scanning', confidence: 0.85 },
      { capability: 'risk_assessment', confidence: 0.85 }
    ],
    relatedTechnologies: ['qualys', 'nmap']
  },
  {
    id: 'palo_alto',
    name: 'Palo Alto Networks',
    aliases: ['Palo Alto', 'PAN-OS', 'Palo Alto Firewall', 'Prisma'],
    category: 'security',
    proves: [
      { capability: 'network_security', confidence: 0.95 },
      { capability: 'firewall_management', confidence: 0.95 },
      { capability: 'threat_prevention', confidence: 0.90 },
      { capability: 'zero_trust_security', confidence: 0.85 }
    ],
    relatedTechnologies: ['cisco_asa', 'fortinet']
  },

  // ============================================================
  // DATA & ANALYTICS
  // ============================================================
  {
    id: 'tableau',
    name: 'Tableau',
    aliases: ['Tableau Desktop', 'Tableau Server', 'Tableau Online'],
    category: 'data',
    proves: [
      { capability: 'data_visualization', confidence: 0.95 },
      { capability: 'business_intelligence', confidence: 0.90 },
      { capability: 'data_analytics', confidence: 0.90 },
      { capability: 'dashboard_development', confidence: 0.95 },
      { capability: 'reporting', confidence: 0.90 }
    ],
    relatedTechnologies: ['power_bi', 'qlik']
  },
  {
    id: 'power_bi',
    name: 'Power BI',
    aliases: ['Microsoft Power BI', 'PowerBI', 'PBI'],
    category: 'data',
    proves: [
      { capability: 'data_visualization', confidence: 0.95 },
      { capability: 'business_intelligence', confidence: 0.90 },
      { capability: 'microsoft_technologies', confidence: 0.85 },
      { capability: 'dashboard_development', confidence: 0.95 },
      { capability: 'data_analytics', confidence: 0.85 }
    ],
    relatedTechnologies: ['tableau', 'azure', 'sql_server']
  },
  {
    id: 'spark',
    name: 'Apache Spark',
    aliases: ['Spark', 'PySpark', 'Spark SQL'],
    category: 'data',
    proves: [
      { capability: 'big_data', confidence: 0.95 },
      { capability: 'distributed_computing', confidence: 0.95 },
      { capability: 'data_processing', confidence: 0.95 },
      { capability: 'data_engineering', confidence: 0.90 },
      { capability: 'real_time_analytics', confidence: 0.85 }
    ],
    relatedTechnologies: ['hadoop', 'databricks', 'kafka']
  },

  // ============================================================
  // AI/ML TECHNOLOGIES
  // ============================================================
  {
    id: 'tensorflow',
    name: 'TensorFlow',
    aliases: ['TF', 'TensorFlow 2', 'TF2'],
    category: 'ai_ml',
    proves: [
      { capability: 'machine_learning', confidence: 0.95 },
      { capability: 'deep_learning', confidence: 0.95 },
      { capability: 'ai_development', confidence: 0.90 },
      { capability: 'model_training', confidence: 0.90 },
      { capability: 'neural_networks', confidence: 0.90 }
    ],
    relatedTechnologies: ['pytorch', 'keras', 'python']
  },
  {
    id: 'pytorch',
    name: 'PyTorch',
    aliases: ['Torch', 'PyTorch Lightning'],
    category: 'ai_ml',
    proves: [
      { capability: 'machine_learning', confidence: 0.95 },
      { capability: 'deep_learning', confidence: 0.95 },
      { capability: 'ai_development', confidence: 0.90 },
      { capability: 'research_ml', confidence: 0.90 },
      { capability: 'neural_networks', confidence: 0.90 }
    ],
    relatedTechnologies: ['tensorflow', 'python']
  },

  // ============================================================
  // PROJECT MANAGEMENT & COLLABORATION
  // ============================================================
  {
    id: 'jira',
    name: 'Jira',
    aliases: ['Atlassian Jira', 'Jira Software', 'Jira Cloud'],
    category: 'project_management',
    proves: [
      { capability: 'agile_project_management', confidence: 0.90 },
      { capability: 'issue_tracking', confidence: 0.95 },
      { capability: 'scrum', confidence: 0.85 },
      { capability: 'kanban', confidence: 0.85 },
      { capability: 'project_tracking', confidence: 0.90 }
    ],
    relatedTechnologies: ['confluence', 'trello']
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    aliases: ['SNOW', 'ServiceNow ITSM', 'ServiceNow Platform'],
    category: 'project_management',
    proves: [
      { capability: 'it_service_management', confidence: 0.95 },
      { capability: 'itsm', confidence: 0.95 },
      { capability: 'workflow_automation', confidence: 0.90 },
      { capability: 'incident_management', confidence: 0.90 },
      { capability: 'change_management', confidence: 0.90 }
    ],
    relatedTechnologies: ['jira', 'bmc_remedy']
  },
  {
    id: 'sharepoint',
    name: 'SharePoint',
    aliases: ['Microsoft SharePoint', 'SharePoint Online', 'SPO'],
    category: 'project_management',
    proves: [
      { capability: 'document_management', confidence: 0.95 },
      { capability: 'collaboration_platforms', confidence: 0.90 },
      { capability: 'microsoft_technologies', confidence: 0.90 },
      { capability: 'content_management', confidence: 0.85 },
      { capability: 'intranet_development', confidence: 0.85 }
    ],
    relatedTechnologies: ['microsoft_365', 'teams', 'power_platform']
  },
  {
    id: 'ms_teams',
    name: 'Microsoft Teams',
    aliases: ['Teams', 'MS Teams', 'Microsoft Teams'],
    category: 'project_management',
    proves: [
      { capability: 'collaboration_platforms', confidence: 0.90 },
      { capability: 'unified_communications', confidence: 0.90 },
      { capability: 'microsoft_technologies', confidence: 0.85 },
      { capability: 'remote_collaboration', confidence: 0.90 }
    ],
    relatedTechnologies: ['sharepoint', 'microsoft_365']
  },

  // ============================================================
  // NETWORKING
  // ============================================================
  {
    id: 'cisco',
    name: 'Cisco Technologies',
    aliases: ['Cisco', 'Cisco IOS', 'Cisco Networking', 'Cisco ASA'],
    category: 'networking',
    proves: [
      { capability: 'network_engineering', confidence: 0.95 },
      { capability: 'network_infrastructure', confidence: 0.95 },
      { capability: 'routing_switching', confidence: 0.95 },
      { capability: 'network_security', confidence: 0.85 },
      { capability: 'enterprise_networking', confidence: 0.90 }
    ],
    relatedTechnologies: ['juniper', 'palo_alto']
  },
  {
    id: 'vmware',
    name: 'VMware',
    aliases: ['VMware vSphere', 'vSphere', 'ESXi', 'vCenter', 'VMware NSX'],
    category: 'networking',
    proves: [
      { capability: 'virtualization', confidence: 0.95 },
      { capability: 'virtual_infrastructure', confidence: 0.95 },
      { capability: 'data_center_operations', confidence: 0.90 },
      { capability: 'server_management', confidence: 0.90 },
      { capability: 'network_virtualization', confidence: 0.85 }
    ],
    relatedTechnologies: ['hyper_v', 'kvm', 'docker']
  },

  // ============================================================
  // OPERATING SYSTEMS
  // ============================================================
  {
    id: 'linux',
    name: 'Linux',
    aliases: ['RHEL', 'Red Hat', 'Ubuntu', 'CentOS', 'Debian', 'Linux Administration'],
    category: 'operating_systems',
    proves: [
      { capability: 'linux_administration', confidence: 0.95 },
      { capability: 'system_administration', confidence: 0.90 },
      { capability: 'server_management', confidence: 0.90 },
      { capability: 'open_source_technologies', confidence: 0.90 },
      { capability: 'shell_scripting', confidence: 0.85 }
    ],
    relatedTechnologies: ['bash', 'docker', 'ansible']
  },
  {
    id: 'windows_server',
    name: 'Windows Server',
    aliases: ['Windows Server 2019', 'Windows Server 2022', 'Active Directory', 'AD'],
    category: 'operating_systems',
    proves: [
      { capability: 'windows_administration', confidence: 0.95 },
      { capability: 'system_administration', confidence: 0.90 },
      { capability: 'active_directory', confidence: 0.90 },
      { capability: 'microsoft_technologies', confidence: 0.90 },
      { capability: 'enterprise_infrastructure', confidence: 0.85 }
    ],
    relatedTechnologies: ['azure', 'dotnet', 'sql_server']
  }
];

/**
 * Find a technology by ID, name, or alias
 */
export function findTechnology(term: string): TechnologyNode | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  return TECHNOLOGY_HIERARCHY.find(tech =>
    tech.id === normalizedTerm ||
    tech.name.toLowerCase() === normalizedTerm ||
    tech.aliases.some(alias => alias.toLowerCase() === normalizedTerm)
  );
}

/**
 * Find all technologies matching a search term (partial match)
 */
export function searchTechnologies(term: string): TechnologyNode[] {
  const normalizedTerm = term.toLowerCase().trim();

  return TECHNOLOGY_HIERARCHY.filter(tech =>
    tech.id.includes(normalizedTerm) ||
    tech.name.toLowerCase().includes(normalizedTerm) ||
    tech.aliases.some(alias => alias.toLowerCase().includes(normalizedTerm))
  );
}

/**
 * Get technologies by category
 */
export function getTechnologiesByCategory(category: string): TechnologyNode[] {
  return TECHNOLOGY_HIERARCHY.filter(tech => tech.category === category);
}

/**
 * Get all capability IDs that technologies can prove
 */
export function getAllTechnologyCapabilities(): string[] {
  const capabilities = new Set<string>();

  for (const tech of TECHNOLOGY_HIERARCHY) {
    for (const proof of tech.proves) {
      capabilities.add(proof.capability);
    }
  }

  return Array.from(capabilities).sort();
}
